import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createBrandConfig,
  deleteBrandConfig,
  fetchActiveBrandId,
  fetchBrandConfig,
  fetchBrandsList,
  saveBrandConfig,
  setActiveBrandId,
} from "../services/brandsApi";
import type { BrandConfig, BrandIdentity } from "@/config/brand";

export const brandsKeys = {
  all: ["brands"] as const,
  list: () => ["brands", "list"] as const,
  active: () => ["brands", "active"] as const,
  detail: (id: string | null) => ["brands", "detail", id] as const,
};

/** GET /brands/ — list all brands. */
export function useBrandsList() {
  return useQuery({
    queryKey: brandsKeys.list(),
    queryFn: ({ signal }) => fetchBrandsList(signal),
  });
}

/** GET /brands/active/ — currently active brand id. */
export function useActiveBrandId() {
  return useQuery({
    queryKey: brandsKeys.active(),
    queryFn: ({ signal }) => fetchActiveBrandId(signal),
  });
}

/** GET /brands/:id/ — full brand config. Disabled when id is null. */
export function useBrandConfig(id: string | null) {
  return useQuery({
    queryKey: brandsKeys.detail(id),
    queryFn: ({ signal }) => fetchBrandConfig(id!, signal),
    enabled: !!id,
  });
}

/** PUT /brands/active/ — invalidates the active-brand query on success. */
export function useSetActiveBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: setActiveBrandId,
    onSuccess: () => qc.invalidateQueries({ queryKey: brandsKeys.active() }),
  });
}

/** PUT /brands/:id/ — invalidates the brand list + that brand's detail. */
export function useSaveBrand() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: saveBrandConfig,
    onSuccess: (_data, brand: BrandConfig) => {
      qc.invalidateQueries({ queryKey: brandsKeys.list() });
      qc.invalidateQueries({ queryKey: brandsKeys.detail(brand.identity.id) });
    },
  });
}

/** POST /brands/ — invalidates the brand list. */
export function useCreateBrand() {
  const qc = useQueryClient();
  return useMutation<BrandIdentity, Error, BrandConfig>({
    mutationFn: createBrandConfig,
    onSuccess: () => qc.invalidateQueries({ queryKey: brandsKeys.list() }),
  });
}

/** DELETE /brands/:id/ — invalidates the brand list + drops the detail cache. */
export function useDeleteBrand() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: deleteBrandConfig,
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: brandsKeys.list() });
      qc.removeQueries({ queryKey: brandsKeys.detail(id) });
    },
  });
}
